import {
  DeleteIcon,
  DragHandleIcon,
  EditIcon,
  InfoIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Flex,
  HStack,
  Input,
  Text,
  Textarea,
  Tooltip,
} from "@chakra-ui/react";
import { useMemo } from "react";
import ReactSelect from "react-select";
import countryList from "react-select-country-list";
import { useLazyDropdown } from "../../hooks/useLazyDropdown";
import { usStateOptions } from "../../utils/case/usStateOption";
import { selectStyles } from "../../utils/selectStyel";
import DynamicSelectField from "../Common/DynamicSelectField";
import { CheckboxList } from "./MultiSelectCheckbox";
import { MultiSelectRadio } from "./MultiSelectRadio";
import MultiSelectWithSelectAllCheckbox from "./MultiSelectWithCheckbox";

export const FieldEditor = ({
  label,
  value,
  isEditable = false,
  onChange,
  type,
  tooltip,
  code,
  referenceMap,
  onEdit,
  onDeleteItem,
  isVisible,
  dragListeners,
  dragAttributes,
  isDragAble,
  MULTISELECT_TYPE,
}) => {
  const countryOptions = useMemo(() => countryList().getData(), []);
  let inferredType = "text";
  const typeUpper = String(type || "").toUpperCase();
  const labelUpper = String(label || "").toUpperCase();
  const isTypeDate = typeUpper === "DATE";
  const isTypeDateTime = typeUpper === "DATETIME";
  const isLabelClosedDate = labelUpper.includes("CLOSED DATE");

  if (isTypeDateTime) {
    inferredType = "datetime-local";
  } else if (isTypeDate) {
    inferredType = isLabelClosedDate ? "datetime-local" : "date";
  } else {
    inferredType = "text";
  }
  const { options, load, loading } = useLazyDropdown(
    code,
    referenceMap?.TIMESTAMP,
    referenceMap?.SUBGROUP
  );
  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      p={2}
      boxShadow="md"
      bg={!isVisible ? "gray.100" : "white"}
      opacity={!isVisible ? 0.6 : 1}
      _hover={isVisible ? { boxShadow: "lg" } : {}}
    >
      <Flex justify="space-between" align="center" mb={2}>
        <Flex alignItems="center" gap={2}>
          <Text fontWeight={400} fontSize="md">
            {label}
          </Text>
          {tooltip && (
            <Tooltip label={tooltip} fontSize="md" hasArrow placement="top">
              <InfoIcon />
            </Tooltip>
          )}
        </Flex>

        <HStack spacing={2}>
          {isDragAble && (
            <DragHandleIcon
              cursor="grab"
              {...dragListeners}
              {...dragAttributes}
            />
          )}
          <EditIcon cursor="pointer" onClick={onEdit} />
          <DeleteIcon color="red" cursor="pointer" onClick={onDeleteItem} />
        </HStack>
      </Flex>

      {/* Render input/select/textarea */}
      {type === "Dropdown" ? (
        <DynamicSelectField
          code={code}
          referenceMap={referenceMap}
          value={value ?? ""}
          isEditable={true}
          options={options}
          loading={loading}
          load={load}
        />
      ) : inferredType === "date" || inferredType === "datetime-local" ? (
        <Input
          type={inferredType}
          disabled={!isEditable}
          borderRadius="md"
          borderColor="gray.600"
          bg="white"
        />
      ) : label === "State" ? (
        <ReactSelect
          options={usStateOptions}
          placeholder="Select State"
          isClearable
          isDisabled={!isEditable}
          value={usStateOptions.find((opt) => opt.value === value) || null}
        />
      ) : label === "Country" || type === "Country" ? (
        <ReactSelect
          options={countryOptions}
          placeholder="Select Country"
          styles={selectStyles}
          menuPortalTarget={document.body}
          isClearable
          isDisabled={!isEditable}
          value={countryOptions.find((opt) => opt.value === value) || null}
        />
      ) : type === "TextBox" ? (
        <Textarea
          value={value ?? ""}
          disabled={!isEditable}
          onChange={(e) => onChange?.(e.target.value)}
          height="20px"
        />
      ) : type === "Multiselect" && MULTISELECT_TYPE === "Checkbox" ? (
        <CheckboxList
          value={value ?? ""}
          disabled={!isEditable}
          onChange={(e) => onChange?.(e.target.value)}
          options={options}
          load={load}
          loading={loading}
        />
      ) : type === "Multiselect" && MULTISELECT_TYPE === "Radio" ? (
        <MultiSelectRadio
          options={options}
          load={load}
          loading={loading}
          disabled={!isEditable}
          onChange={(e) => onChange?.(e.target.value)}
          value={value ?? ""}
        />
      ) : type === "Multiselect" &&
        MULTISELECT_TYPE === "Select_All_With_CheckBox" ? (
        <MultiSelectWithSelectAllCheckbox
          options={options}
          load={load}
          loading={loading}
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="Sample Multi Select"
          disabled={!isEditable}
        />
      ) : type === "Date" ? (
        <Input
          type="date"
          value={value ?? ""}
          disabled={!isEditable}
          onChange={(e) => onChange?.(e.target.value)}
          borderRadius="md"
          borderColor="gray.500"
          bg="white"
        />
      ) : (
        <Input
          type={type === "Numeric" || "Number" ? "number" : "text"}
          value={value ?? ""}
          disabled={!isEditable}
          onChange={(e) => onChange?.(e.target.value)}
          step={type === "Numeric" ? "any" : undefined}
          borderRadius="md"
          borderColor="gray.500"
          bg="white"
        />
      )}
    </Box>
  );
};
